import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { StockCategoryFilter } from '../types/stockCategories';
import { GuessSolution, isGuessForSolution } from '../utils/guessMatching';

/** Row shape returned by the get_top_guesses RPC. */
interface TopGuessRow {
  guess_text: string;
  guess_percentage: number;
  /**
   * Not used. The RPC is readable with the public key, so the server will
   * stop revealing this for the current game date. Correctness is computed
   * on the client from the finished game's solution instead.
   */
  is_correct?: boolean;
}

export interface TopGuess {
  guess_text: string;
  guess_percentage: number;
  /** Whether this guess names the solution, computed from `solution`. */
  isCorrect: boolean;
}

interface UseTopGuessesOptions {
  category: StockCategoryFilter;
  gameDate: string;
  /** The solution for `gameDate` (today's or an archive day's). Required to fetch. */
  solution: GuessSolution | null | undefined;
  enabled?: boolean;
}

/**
 * Fetches the top 3 most common guesses for a category/date from game_tracking.
 * Uses the get_top_guesses RPC function which bypasses RLS for aggregation.
 * Only meant for a finished game: the caller passes that day's solution, and
 * each guess is marked correct by comparing it to the solution.
 */
export const useTopGuesses = ({
  category,
  gameDate,
  solution,
  enabled = true
}: UseTopGuessesOptions) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['topGuesses', category, gameDate],
    queryFn: async () => {
      const { data, error: rpcError } = await supabase.rpc('get_top_guesses', {
        p_category: category,
        p_game_date: gameDate
      });

      if (rpcError) {
        console.error('[useTopGuesses] RPC Error:', rpcError);
        throw rpcError;
      }

      return (data || []) as TopGuessRow[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache
    enabled: enabled && Boolean(solution?.Symbol),
    refetchOnWindowFocus: false, // No need to refetch on focus for historical data
  });

  const solutionSymbol = solution?.Symbol;
  const solutionName = solution?.Security;

  const topGuesses = useMemo<TopGuess[]>(
    () =>
      (data || []).map(row => ({
        guess_text: row.guess_text,
        guess_percentage: row.guess_percentage,
        isCorrect: isGuessForSolution(
          row.guess_text,
          solutionSymbol ? { Symbol: solutionSymbol, Security: solutionName } : null
        )
      })),
    [data, solutionSymbol, solutionName]
  );

  return {
    topGuesses,
    isLoading,
    error: error instanceof Error ? error.message : null,
    refetch
  };
};

export default useTopGuesses;
